import { AlertTriangle, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ContingencyReason, GeoStatus } from '../types';

interface LocationStatusPillProps {
  location: { lat: number; lng: number } | null;
  status: GeoStatus;
  /** Whether the agreement lets contingency states release the punch. */
  contingencyAllowed: boolean;
  /** True while the degraded second attempt is in flight. */
  retrying?: boolean;
  className?: string;
}

// Contingência é um estado PERMITIDO — o registro segue. As 3 chaves abaixo (timeout,
// unavailable, inaccurate = ContingencyReason) lideram com o efeito ("registro liberado")
// antes da causa, para o trabalhador não confundir contingência com bloqueio — ver
// PLATFORM-3333. As demais chaves (unsupported, permission-prompt, permission-denied) não
// são contingência: continuam bloqueando o registro, então mantêm o texto de causa.
const ERROR_LABELS: Record<Exclude<GeoStatus, 'loading' | 'success'>, string> = {
  timeout: 'Registro liberado: localização não obtida, a coleta seguirá sob contingência',
  unavailable: 'Registro liberado: localização não obtida, a coleta seguirá sob contingência',
  unsupported: 'Navegador sem suporte à localização. A coleta não será autorizada',
  'permission-prompt': 'Aguardando permissão de localização do navegador',
  'permission-denied': 'Permissão do navegador negada. A coleta não será autorizada',
  inaccurate: 'Registro liberado: localização aproximada, a coleta seguirá sob contingência',
};

/**
 * Quando o acordo exige localização (`required`), os estados de contingência deixam de
 * liberar o registro: o texto passa a dizer que a coleta não será autorizada, em vermelho.
 */
const REQUIRED_BLOCK_LABELS: Record<ContingencyReason, string> = {
  timeout: 'Localização não obtida. Este acordo exige localização: a coleta não será autorizada',
  unavailable: 'Localização não obtida. Este acordo exige localização: a coleta não será autorizada',
  inaccurate: 'Localização imprecisa. Este acordo exige localização precisa: a coleta não será autorizada',
};

function isContingencyReason(status: GeoStatus): status is ContingencyReason {
  return status in REQUIRED_BLOCK_LABELS;
}

/**
 * Tom de cada falha. Contingência é um estado PERMITIDO — o registro segue — e
 * `permission-prompt` é transitório: pintá-los de erro alarma o trabalhador sem
 * motivo. Só o que de fato impede o registro é vermelho.
 */
const FAILURE_TONE: Record<Exclude<GeoStatus, 'loading' | 'success'>, 'warning' | 'error'> = {
  timeout: 'warning',
  unavailable: 'warning',
  inaccurate: 'warning',
  'permission-prompt': 'warning',
  'permission-denied': 'error',
  unsupported: 'error',
};

/**
 * Estado da geolocalização no registro de ponto.
 *
 * Os estados curtos (obtendo, confirmada) cabem numa pill. As mensagens de
 * falha não: são frases inteiras, e a pill do design system é
 * `whitespace-nowrap` + `overflow-hidden`, então elas rompiam o padding do card
 * e ficavam CORTADAS — 437px de largura numa tela de 390px, medido. Por isso a
 * falha usa a caixa de aviso já consagrada no projeto (ver AgreementsPage), que
 * quebra linha e respeita o container.
 */
export function LocationStatusPill({
  location,
  status,
  contingencyAllowed,
  retrying = false,
  className,
}: LocationStatusPillProps) {
  if (status === 'loading') {
    return (
      <Badge
        variant="secondary"
        className={cn(
          'h-auto max-w-full gap-1.5 whitespace-normal rounded-full px-3 py-1.5 text-center text-xs font-medium',
          className,
        )}
      >
        <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {retrying ? 'Sinal fraco, tentando leitura aproximada…' : 'Obtendo localização…'}
      </Badge>
    );
  }

  if (status !== 'success') {
    const blockedContingency = !contingencyAllowed && isContingencyReason(status);
    const tone = blockedContingency ? 'error' : FAILURE_TONE[status];
    const label = blockedContingency ? REQUIRED_BLOCK_LABELS[status] : ERROR_LABELS[status];
    return (
      <div
        role="alert"
        aria-live="polite"
        className={cn(
          'flex w-full gap-3 rounded-xl border px-4 py-3 text-left',
          tone === 'warning'
            ? 'border-amber-200 bg-amber-50 dark:border-amber-800/40 dark:bg-amber-950/40'
            : 'border-red-200 bg-red-50 dark:border-red-800/40 dark:bg-red-950/40',
          className,
        )}
      >
        <AlertTriangle
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0',
            tone === 'warning'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-red-600 dark:text-red-400',
          )}
          aria-hidden="true"
        />
        <p
          className={cn(
            'text-sm',
            tone === 'warning'
              ? 'text-amber-800 dark:text-amber-200'
              : 'text-red-800 dark:text-red-200',
          )}
        >
          {label}
        </p>
      </div>
    );
  }

  return (
    <Badge
      variant="secondary"
      className={cn(
        'h-auto max-w-full gap-1.5 whitespace-normal rounded-full px-3 py-1.5 text-xs font-medium',
        className,
      )}
      aria-label={
        location
          ? `Localização: ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`
          : 'Localização confirmada'
      }
    >
      <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      Localização confirmada
    </Badge>
  );
}
